import React, { useEffect, useMemo, useState } from "react";
import {
  FaBuilding,
  FaCheck,
  FaTimes,
  FaChartLine,
  FaSearch,
  FaTimes as FaClose,
  FaRegFilePdf,
  FaFileExcel,
} from "react-icons/fa";
import { fetchDailyOccupancy } from "../../../Store/Apis/ReservationApi";
import downloadPDF from "../../../Hooks/downloadReportPdf";
import formatDate from "../../../Hooks/formatDate";
import * as XLSX from "xlsx";
import RoomOccupancyChart from "../../../Components/Charts/RoomOccupancy";
import VenueOccupancyChart from "../../../Components/Charts/VenueOccupancy";

const DailyOccupancy = () => {
  const [dateType, setDateType] = useState("single");
  // Default to today's date (YYYY-MM-DD) for HTML date input compatibility
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [singleDate, setSingleDate] = useState(today);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 6;
  const [activeTab, setActiveTab] = useState("table-rooms"); // "table-rooms", "table-venues", "chart-rooms", or "chart-venues"

  // Live data state
  const [occupancyData, setOccupancyData] = useState({
    totalRooms: 0,
    totalOccupied: 0,
    totalUnoccupied: 0,
    occupiedPercentage: 0,
  });
  const [roomData, setRoomData] = useState([]);

  // Reset current page when switching tabs
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Filter functions for rooms and venues
  const filterRoomsOnly = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(
      (item) => item.roomList && item.roomList.toLowerCase().includes("room"),
    );
  };

  const filterVenuesOnly = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(
      (item) => item.roomList && !item.roomList.toLowerCase().includes("room"),
    );
  };

  // Get filtered data based on current view
  const roomsData = filterRoomsOnly(roomData);
  const venuesData = filterVenuesOnly(roomData);

  // Normalize backend response into UI shape
  const normalizeData = (raw) => {
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.rooms)
          ? raw.rooms
          : Array.isArray(raw?.items)
            ? raw.items
            : Array.isArray(raw?.list)
              ? raw.list
              : [];

    const mapped = list.map((item, idx) => {
      // New API structure: status 1 = occupied, 0 = unoccupied
      const occupied = item?.status === 1;
      const desc = (item?.statusDescription || "").trim().toLowerCase();

      const outletName = "—"; // Not provided in new API
      const roomName = item.roomTypeName || "—";

      // For occupied rooms, get customer from first booking (if available)
      const firstBooking =
        occupied && item.bookings && item.bookings.length > 0
          ? item.bookings[0]
          : null;
      const customer = firstBooking ? firstBooking.customer_name || "—" : "—";

      // Build bookingDetails from first booking when occupied
      const bookingDetails = firstBooking
        ? {
            roomTypeName: firstBooking.room_type_name || roomName || "—",
            roomName: firstBooking.service_name || "—",
            statusDescription: item.statusDescription || "Occupied",
            checkInDate:
              firstBooking.checkInDate || firstBooking.order_date || null,
            checkOutDate:
              firstBooking.checkOutDate || firstBooking.check_out_date || null,
            customerName: firstBooking.customer_name || "—",
            orderDate: firstBooking.order_date || null,
            checkOutOrderDate: firstBooking.check_out_date || null,
            orderStatus: firstBooking.order_status || "Confirmed",
          }
        : null;

      return {
        id: item.roomTypeID || idx + 1,
        outletName,
        roomList: roomName,
        customerDetails: customer,
        bookingStatus: occupied ? "OCCUPIED" : "UNOCCUPIED",
        bookingDetails,
      };
    });

    // Natural sort by Room List (ascending): handles "Room 2" < "Room 10"
    const sorted = [...mapped].sort((a, b) =>
      String(a.roomList || "")
        .trim()
        .localeCompare(String(b.roomList || "").trim(), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    );

    const totalRooms = sorted.length;
    const totalOccupied = sorted.filter(
      (r) => r.bookingStatus === "OCCUPIED",
    ).length;
    const totalUnoccupied = totalRooms - totalOccupied;
    const occupiedPercentage = totalRooms
      ? Number(((totalOccupied / totalRooms) * 100).toFixed(2))
      : 0;

    return {
      stats: { totalRooms, totalOccupied, totalUnoccupied, occupiedPercentage },
      rows: sorted,
    };
  };

  const toApiDate = (d) => {
    if (!d) return d;
    // Convert YYYY-MM-DD (HTML date input) to YYYY/MM/DD expected by API/UI
    return d.includes("-") ? d.replaceAll("-", "/") : d;
  };

  const runFetch = async (params) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchDailyOccupancy({
        singleDate: toApiDate(params.singleDate),
        startDate: toApiDate(params.startDate),
        endDate: toApiDate(params.endDate),
      });
      // Success when HTTP 200 and backend-style status ok if present
      const ok =
        res?.status === 200 || res?.status === 1 || res?.success === true;
      if (!ok) {
        setRoomData([]);
        setOccupancyData({
          totalRooms: 0,
          totalOccupied: 0,
          totalUnoccupied: 0,
          occupiedPercentage: 0,
        });
        setError(res?.message || "Failed to load occupancy.");
        return;
      }

      const normalized = normalizeData(res);
      setRoomData(normalized.rows);
      setOccupancyData(normalized.stats);
      setCurrentPage(1);
    } catch (e) {
      console.error("DailyOccupancy load error", e);
      setError(e?.message || "Unexpected error while loading occupancy.");
      setRoomData([]);
      setOccupancyData({
        totalRooms: 0,
        totalOccupied: 0,
        totalUnoccupied: 0,
        occupiedPercentage: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load for today
  useEffect(() => {
    runFetch({ singleDate: today });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today]);

  const handleViewBookingDetails = (room) => {
    if (room.bookingDetails) {
      setSelectedBooking(room.bookingDetails);
      setShowModal(true);
    }
  };

  const formatDateLocal = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get current filtered data based on active tab for PDF
  const getCurrentDataForPDF = () => {
    if (activeTab === "table-rooms") {
      return roomsData;
    } else if (activeTab === "table-venues") {
      return venuesData;
    }
    return roomData; // fallback for other tabs
  };

  // Get current tab type for export names
  const getCurrentTabTypeForPDF = () => {
    if (activeTab === "table-rooms") {
      return "Rooms";
    } else if (activeTab === "table-venues") {
      return "Venues";
    }
    return "Occupancy";
  };

  // PDF Download preparation - follow same pattern as other reports
  const currentDataForPDF = getCurrentDataForPDF();
  const rows =
    Array.isArray(currentDataForPDF) && currentDataForPDF.length > 0
      ? currentDataForPDF.map((item, index) => [
          index + 1,
          // item.outletName || "—",
          item.roomList || "—",
          // item.customerDetails || "—",
          item.bookingStatus || "UNOCCUPIED",
        ])
      : [];
  const columns = [
    "Sl No.",
    // "Outlet Name",
    `${getCurrentTabTypeForPDF()} List`,
    // "Customer Details",
    "Booking Status",
  ];

  // Excel Download method - follow same pattern as CustomerOrders
  const downloadExcel = () => {
    // Get current filtered data based on active tab
    const getCurrentDataForExcel = () => {
      if (activeTab === "table-rooms") {
        return roomsData;
      } else if (activeTab === "table-venues") {
        return venuesData;
      }
      return roomData; // fallback for other tabs
    };

    // Get current tab type for export names
    const getCurrentTabTypeForExcel = () => {
      if (activeTab === "table-rooms") {
        return "Rooms";
      } else if (activeTab === "table-venues") {
        return "Venues";
      }
      return "Occupancy";
    };

    const currentDataForExcel = getCurrentDataForExcel();
    const tabTypeForExcel = getCurrentTabTypeForExcel();

    if (
      !currentDataForExcel ||
      !Array.isArray(currentDataForExcel) ||
      currentDataForExcel.length === 0
    ) {
      alert(`No ${tabTypeForExcel.toLowerCase()} data available for download`);
      return;
    }

    // Prepare data for Excel - same pattern as CustomerOrders
    const excelData = currentDataForExcel.map((item, index) => ({
      "Sl No.": index + 1,
      // "Outlet Name": item.outletName || "",
      [`${tabTypeForExcel} List`]: item.roomList || "",
      // "Customer Details": item.customerDetails || "",
      "Booking Status": item.bookingStatus || "",
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better formatting
    const columnWidths = [
      { wch: 8 }, // Sl No.
      { wch: 20 }, // Room/Venue List
      { wch: 15 }, // Booking Status
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `Daily ${tabTypeForExcel} Occupancy Report`,
    );

    // Generate filename with date range - same pattern as CustomerOrders
    const filename = `Daily_${tabTypeForExcel}_Occupancy_Report_${formatDate(
      dateType === "single" ? singleDate : startDate,
    )}-${formatDate(dateType === "single" ? singleDate : endDate)}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };

  const StatCard = ({ title, value, icon: IconComponent, bgColor }) => (
    <div className="col-lg-3 col-md-6 col-12 mb-3">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <p className="text-muted text-uppercase text-sm mb-1 fw-500">
                {title}
              </p>
              <h4 className="fw-bold mb-0 text-dark">{value}</h4>
            </div>
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center ${bgColor}`}
              style={{ width: "50px", height: "50px" }}
            >
              <IconComponent className="text-white" size={20} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleSearch = () => {
    if (dateType === "single") {
      if (!singleDate) {
        setError("Please select a date.");
        return;
      }
      runFetch({ singleDate });
    } else {
      if (!startDate || !endDate) {
        setError("Please select a start and end date.");
        return;
      }
      runFetch({ startDate, endDate });
    }
  };

  // Pagination helpers - work with filtered data based on active tab
  const getCurrentData = () => {
    if (activeTab === "table-rooms") return roomsData;
    if (activeTab === "table-venues") return venuesData;
    return roomData;
  };

  const currentData = getCurrentData();
  const totalPages = Math.max(1, Math.ceil(currentData.length / rowsPerPage));
  const startIdx = (currentPage - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const paginatedRoomData = currentData.slice(startIdx, endIdx);

  return (
    <div
      className="container-fluid p-4"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      <div className="row align-items-center justify-content-between mb-4">
        <div className="col">
          <h3 className="fw-600 text-dark mb-0">Daily Occupancy Report</h3>
        </div>
      </div>

      {/* Date Selection Card */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-4">
          <div className="row align-items-end">
            <div className="col-md-3 mb-3">
              <label className="form-label text-muted text-sm fw-500">
                Select Date Type
              </label>
              <select
                className="form-select border-2"
                value={dateType}
                onChange={(e) => setDateType(e.target.value)}
                style={{ borderColor: "#e9ecef" }}
              >
                <option value="single">Single Date</option>
                <option value="range">Date Range</option>
              </select>
            </div>

            {dateType === "single" ? (
              <div className="col-md-3 mb-3">
                <label className="form-label text-muted text-sm fw-500">
                  Date
                </label>
                <input
                  type="date"
                  className="form-control border-2"
                  value={singleDate}
                  onChange={(e) => setSingleDate(e.target.value)}
                  style={{ borderColor: "#e9ecef" }}
                />
              </div>
            ) : (
              <>
                <div className="col-md-3 mb-3">
                  <label className="form-label text-muted text-sm fw-500">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control border-2"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ borderColor: "#e9ecef" }}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label text-muted text-sm fw-500">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control border-2"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ borderColor: "#e9ecef" }}
                  />
                </div>
              </>
            )}

            <div className="col-md-3 mb-1">
              <button
                className="btn btn-primary px-4  py-2 fw-500"
                onClick={handleSearch}
                disabled={loading}
              >
                <FaSearch className="me-2" />
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
          {!!error && (
            <div className="alert alert-warning mt-3 mb-0 py-2" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <StatCard
          title="Total Rooms"
          value={occupancyData.totalRooms}
          icon={FaBuilding}
          bgColor="bg-warning"
        />
        <StatCard
          title="Total Occupied"
          value={occupancyData.totalOccupied}
          icon={FaCheck}
          bgColor="bg-success"
        />
        <StatCard
          title="Total Unoccupied"
          value={occupancyData.totalUnoccupied}
          icon={FaTimes}
          bgColor="bg-warning"
        />
        <StatCard
          title="Occupied Percentage"
          value={`${occupancyData.occupiedPercentage}%`}
          icon={FaChartLine}
          bgColor="bg-warning"
        />
      </div>

      {/* Tab Navigation and Content */}
      {roomData && roomData.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              {/* Tab Headers */}
              <div className="card-header bg-white border-bottom">
                <ul className="nav nav-tabs border-0" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === "table-rooms" ? "active" : ""}`}
                      onClick={() => setActiveTab("table-rooms")}
                      type="button"
                      role="tab"
                      style={{
                        border: "none",
                        borderBottom:
                          activeTab === "table-rooms"
                            ? "2px solid #0d6efd"
                            : "none",
                        color:
                          activeTab === "table-rooms" ? "#0d6efd" : "#6c757d",
                        fontWeight: activeTab === "table-rooms" ? "600" : "500",
                      }}
                    >
                      Room Tables
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === "table-venues" ? "active" : ""}`}
                      onClick={() => setActiveTab("table-venues")}
                      type="button"
                      role="tab"
                      style={{
                        border: "none",
                        borderBottom:
                          activeTab === "table-venues"
                            ? "2px solid #0d6efd"
                            : "none",
                        color:
                          activeTab === "table-venues" ? "#0d6efd" : "#6c757d",
                        fontWeight:
                          activeTab === "table-venues" ? "600" : "500",
                      }}
                    >
                      Venue Tables
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === "chart-rooms" ? "active" : ""}`}
                      onClick={() => setActiveTab("chart-rooms")}
                      type="button"
                      role="tab"
                      style={{
                        border: "none",
                        borderBottom:
                          activeTab === "chart-rooms"
                            ? "2px solid #0d6efd"
                            : "none",
                        color:
                          activeTab === "chart-rooms" ? "#0d6efd" : "#6c757d",
                        fontWeight: activeTab === "chart-rooms" ? "600" : "500",
                      }}
                    >
                      Room Charts
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === "chart-venues" ? "active" : ""}`}
                      onClick={() => setActiveTab("chart-venues")}
                      type="button"
                      role="tab"
                      style={{
                        border: "none",
                        borderBottom:
                          activeTab === "chart-venues"
                            ? "2px solid #0d6efd"
                            : "none",
                        color:
                          activeTab === "chart-venues" ? "#0d6efd" : "#6c757d",
                        fontWeight:
                          activeTab === "chart-venues" ? "600" : "500",
                      }}
                    >
                      Venue Charts
                    </button>
                  </li>
                </ul>
              </div>

              {/* Download Buttons */}
              <div className="card-header bg-white border-0 pt-3 pb-0">
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-success me-2 btn-sm"
                    onClick={() =>
                      downloadPDF({
                        title: `Daily ${getCurrentTabTypeForPDF()} Occupancy Report`,
                        docName: `Daily_${getCurrentTabTypeForPDF()}_Occupancy_Report ${formatDate(
                          dateType === "single" ? singleDate : startDate,
                        )}-${formatDate(
                          dateType === "single" ? singleDate : endDate,
                        )}`,
                        startDate: formatDate(
                          dateType === "single" ? singleDate : startDate,
                        ),
                        endDate: formatDate(
                          dateType === "single" ? singleDate : endDate,
                        ),
                        rows,
                        columns,
                      })
                    }
                    disabled={
                      loading ||
                      !Array.isArray(currentDataForPDF) ||
                      currentDataForPDF.length === 0
                    }
                  >
                    <FaRegFilePdf className="me-1" size={14} />
                    PDF
                  </button>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={downloadExcel}
                    disabled={
                      loading ||
                      !Array.isArray(currentDataForPDF) ||
                      currentDataForPDF.length === 0
                    }
                  >
                    <FaFileExcel className="me-1" size={14} />
                    Excel
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="card-body p-0">
                {activeTab === "table-rooms" || activeTab === "table-venues" ? (
                  /* Table View */
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead style={{ backgroundColor: "#f8f9fa" }}>
                        <tr>
                          <th className="text-muted text-uppercase text-sm fw-600 py-3 px-4 border-0">
                            #
                          </th>
                          <th className="text-muted text-uppercase text-sm fw-600 py-3 px-4 border-0">
                            {activeTab === "table-rooms"
                              ? "Room List"
                              : "Venue List"}
                          </th>
                          <th className="text-muted text-uppercase text-sm fw-600 py-3 px-4 border-0">
                            Booking Status
                          </th>
                          <th className="text-muted text-uppercase text-sm fw-600 py-3 px-4 border-0">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentData.length === 0 && !loading && (
                          <tr>
                            <td
                              colSpan="4"
                              className="py-4 px-4 text-center text-muted"
                            >
                              No{" "}
                              {activeTab === "table-rooms" ? "room" : "venue"}{" "}
                              data for the selected date(s).
                            </td>
                          </tr>
                        )}
                        {paginatedRoomData.map((room) => (
                          <tr
                            key={room.id}
                            className="border-bottom"
                            style={{ borderColor: "#e9ecef" }}
                          >
                            <td className="py-3 px-4">
                              <span className="text-sm text-muted">
                                {room.id}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm fw-500 text-dark">
                                {room.roomList}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`badge px-3 py-2 text-sm fw-500 ${
                                  room.bookingStatus === "OCCUPIED"
                                    ? "bg-success text-white"
                                    : "bg-warning text-dark"
                                }`}
                              >
                                {room.bookingStatus}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                className={`btn btn-sm px-3 py-1 fw-500 ${
                                  room.bookingStatus === "OCCUPIED"
                                    ? "btn-outline-primary"
                                    : "btn-outline-secondary"
                                }`}
                                style={{ fontSize: "0.75rem" }}
                                onClick={() => handleViewBookingDetails(room)}
                                disabled={room.bookingStatus === "UNOCCUPIED"}
                              >
                                VIEW BOOKING DETAILS
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : activeTab === "chart-rooms" ? (
                  /* Room Chart View */
                  <div style={{ minHeight: "400px" }}>
                    <RoomOccupancyChart
                      roomsData={roomData.map((room) => ({
                        roomTypeID: room.id,
                        roomTypeName: room.roomList,
                        status: room.bookingStatus === "OCCUPIED" ? 1 : 0,
                        statusDescription:
                          room.bookingStatus === "OCCUPIED"
                            ? "Occupied"
                            : "Unoccupied",
                        bookings: room.bookingDetails
                          ? [room.bookingDetails]
                          : [],
                      }))}
                    />
                  </div>
                ) : activeTab === "chart-venues" ? (
                  /* Venue Chart View */
                  <div style={{ minHeight: "400px" }}>
                    <VenueOccupancyChart
                      roomsData={roomData.map((room) => ({
                        roomTypeID: room.id,
                        roomTypeName: room.roomList,
                        status: room.bookingStatus === "OCCUPIED" ? 1 : 0,
                        statusDescription:
                          room.bookingStatus === "OCCUPIED"
                            ? "Occupied"
                            : "Unoccupied",
                        bookings: room.bookingDetails
                          ? [room.bookingDetails]
                          : [],
                      }))}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination - only show for table views */}
      {currentData.length > 0 &&
        (activeTab === "table-rooms" || activeTab === "table-venues") && (
          <div className="d-flex justify-content-end align-items-center mt-3">
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    &lt;
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (p) => (
                    <li
                      key={p}
                      className={`page-item ${
                        p === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ),
                )}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                  >
                    &gt;
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}

      {/* Booking Details Modal */}
      {showModal && selectedBooking && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-600 text-dark">
                  Check Booking Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  style={{ background: "none", border: "none" }}
                >
                  <FaClose size={16} className="text-muted" />
                </button>
              </div>
              <div className="modal-body pt-2">
                <div className="table-responsive">
                  <table className="table table-borderless">
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th className="text-muted text-uppercase text-sm fw-600 py-3 px-4 border-0">
                          Service Name
                        </th>
                        <th className="text-muted text-uppercase text-sm fw-600 py-3 px-4 border-0">
                          Service Type
                        </th>
                        <th className="text-muted text-uppercase text-sm fw-600 py-3 px-4 border-0">
                          Booking Dates
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-3 px-4 text-sm fw-500">
                          {selectedBooking.roomName}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {selectedBooking.roomTypeName}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {formatDateLocal(selectedBooking.checkInDate)} -{" "}
                          {formatDateLocal(selectedBooking.checkOutDate)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="row mt-4">
                  <div className="col-md-6 mb-3">
                    <div className="card bg-light border-0">
                      <div className="card-body p-3">
                        <h6 className="text-muted text-uppercase text-sm mb-2">
                          Customer Information
                        </h6>
                        <p className="mb-1">
                          <strong>Name:</strong> {selectedBooking.customerName}
                        </p>
                        <p className="mb-0">
                          <strong>Status:</strong>{" "}
                          {selectedBooking.statusDescription}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="card bg-light border-0">
                      <div className="card-body p-3">
                        <h6 className="text-muted text-uppercase text-sm mb-2">
                          Booking Information
                        </h6>
                        <p className="mb-1">
                          <strong>Order Date:</strong>{" "}
                          {formatDateLocal(selectedBooking.orderDate)}
                        </p>
                        <p className="mb-0">
                          <strong>Order Status:</strong>
                          <span
                            className={`badge ms-2 px-2 py-1 ${
                              selectedBooking.orderStatus === "Confirmed"
                                ? "bg-success"
                                : "bg-warning"
                            }`}
                          >
                            {selectedBooking.orderStatus}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary px-4 py-2"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .text-sm {
          font-size: 0.875rem;
        }
        .fw-500 {
          font-weight: 500;
        }
        .fw-600 {
          font-weight: 600;
        }
        .card {
          transition: all 0.2s ease-in-out;
        }
        .card:hover {
          transform: translateY(-2px);
        }
        .table tbody tr:hover {
          background-color: #f8f9fa;
        }
        .btn:hover {
          transform: translateY(-1px);
        }
        .form-control:focus,
        .form-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }
      `}</style>
    </div>
  );
};

export default DailyOccupancy;
